import type { ManufacturingCase } from '@infinitform/platform-core';
import { computeCaseLabel } from './case-sync.util';

/**
 * Loose shape of a `project.config` payload coming from the backend — includes
 * both the legacy flat fields (single-method projects) and the optional new
 * `manufacturingMethods` array. Matches what `GET /project/:id` returns today
 * (the backend's `config` field is loosely typed as an object blob).
 *
 * `manufacturingMethod` is declared loosely because very old project documents
 * stored it as an array of method-config objects with an `{id, type, ...}`
 * shape; the normalizer below coerces those back into real cases.
 */
export interface LegacyProjectConfigShape {
  manufacturingMethod?: string | LegacyMethodEntry[];
  millingVector?: {
    xp: boolean; yp: boolean; zp: boolean; xm: boolean; ym: boolean; zm: boolean;
  };
  drawDirectionVector?: {
    xp: boolean; yp: boolean; zp: boolean; xm: boolean; ym: boolean; zm: boolean;
  };
  generatePrismatic?: boolean;
  costModel?: {
    axesType: '3-axis' | '4-axis' | '5-axis';
    removalRate: number;
    machiningCostPerHour: number;
    setupCost: number;
  };
  minimumToolSize?: number;
  minimumToolSizeUnit?: 'mm' | 'in';
  ribsDensity?: 'Low' | 'Medium';
  wallThickness?: number | 'auto';
  bottomCutFrac?: number;
  skeletonMethod?: 'Skeleton3D' | 'Skeleton2D';

  manufacturingMethods?: ManufacturingCase[];

  // Passthrough: anything else on config stays untouched.
  [key: string]: unknown;
}

/**
 * Old pre-multi-MF DB shape sometimes found in `config.manufacturingMethod`
 * (array form) or leaked into `manufacturingMethods[i].method` (array form).
 * Uses `id`/`type` instead of `methodId`/`method`.
 */
interface LegacyMethodEntry {
  id?: string;
  type?: string;
  millingVector?: LegacyProjectConfigShape['millingVector'];
  drawDirectionVector?: LegacyProjectConfigShape['drawDirectionVector'];
  minimumToolSize?: number;
  minimumToolSizeUnit?: 'mm' | 'in';
  generatePrismatic?: boolean;
  generateOrganic?: boolean;
  costModel?: LegacyProjectConfigShape['costModel'];
  ribsDensity?: 'Low' | 'Medium';
  wallThickness?: number | 'auto';
  bottomCutFrac?: number;
  skeletonMethod?: 'Skeleton3D' | 'Skeleton2D';
}

/**
 * RFC4122 v4-ish UUID generator. Kept small to avoid adding a dependency.
 * Callers that need a cryptographically stronger UUID can swap this later.
 */
function uuidv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Convert one legacy `{id, type, ...}` entry into a valid `ManufacturingCase`.
 * Missing `type` falls back to Additive Manufacturing so the downstream code
 * always has a string method to branch on.
 */
function caseFromLegacyEntry(entry: LegacyMethodEntry): ManufacturingCase {
  const c: ManufacturingCase = {
    methodId: entry.id ?? uuidv4(),
    method: typeof entry.type === 'string' ? entry.type : 'Additive Manufacturing',
  };
  if (entry.millingVector !== undefined) c.millingVector = entry.millingVector;
  if (entry.drawDirectionVector !== undefined) c.drawDirectionVector = entry.drawDirectionVector;
  if (entry.generatePrismatic !== undefined) c.generatePrismatic = entry.generatePrismatic;
  if (entry.costModel !== undefined) c.costModel = entry.costModel;
  if (entry.minimumToolSize !== undefined) c.minimumToolSize = entry.minimumToolSize;
  if (entry.minimumToolSizeUnit !== undefined) c.minimumToolSizeUnit = entry.minimumToolSizeUnit;
  if (entry.ribsDensity !== undefined) c.ribsDensity = entry.ribsDensity;
  if (entry.wallThickness !== undefined) c.wallThickness = entry.wallThickness;
  if (entry.bottomCutFrac !== undefined) c.bottomCutFrac = entry.bottomCutFrac;
  if (entry.skeletonMethod !== undefined) c.skeletonMethod = entry.skeletonMethod;
  return c;
}

/**
 * Repair a `manufacturingMethods` array whose entries were corrupted by an
 * earlier normalization against an array-shaped legacy `manufacturingMethod`
 * field (symptoms: `method` holds an array of `{id, type, ...}` objects and
 * `label` renders as `"[object Object],[object Object],..."`). If the
 * corruption is present, the legacy entries are extracted into proper cases;
 * otherwise the array is returned untouched.
 */
function repairCorruptedCases(cases: ManufacturingCase[]): ManufacturingCase[] {
  const corrupted = cases.some((c) => Array.isArray((c as { method: unknown }).method));
  if (!corrupted) return cases;

  const repaired: ManufacturingCase[] = [];
  for (const c of cases) {
    const raw = (c as { method: unknown }).method;
    if (Array.isArray(raw)) {
      for (const entry of raw as LegacyMethodEntry[]) {
        repaired.push(caseFromLegacyEntry(entry));
      }
    } else {
      repaired.push(c);
    }
  }
  return repaired;
}

/**
 * If the project's config already has a well-formed `manufacturingMethods[]`,
 * return as-is. Otherwise lift the legacy flat fields into the array. Handles
 * three legacy shapes:
 *
 *  1. `manufacturingMethod: <enum string>` (post-2024 single-method projects)
 *     → wrap into a single-element `manufacturingMethods[0]`.
 *  2. `manufacturingMethod: LegacyMethodEntry[]` (very old projects where the
 *     flat field stored an array of `{id, type, ...}` objects)
 *     → explode into N cases, one per entry.
 *  3. `manufacturingMethods[i].method: LegacyMethodEntry[]` (already-corrupted
 *     projects produced by a prior normalization against shape 2)
 *     → repair by extracting the array entries back into proper cases.
 *
 * The flat legacy fields are left in place (V2 mirror strategy), but
 * `manufacturingMethod` is coerced to a string enum drawn from the first case
 * so downstream XML writers / filters never see an array.
 *
 * Pure function: does not mutate the input. Returns a new object.
 */
export function normalizeToNewShape<T extends LegacyProjectConfigShape>(config: T): T {
  // If `manufacturingMethods` is an explicit array — even an empty one — it is
  // the canonical source of truth. Do NOT fall through to the legacy
  // `manufacturingMethod` field, otherwise deleting the last case would
  // silently revive it from the (now stale) flat singular field on next load.
  if (Array.isArray(config.manufacturingMethods)) {
    if (config.manufacturingMethods.length === 0) {
      // Also clear the stale flat mirror — same trio that
      // `removeManufacturingCase` clears live when the last case is deleted.
      // Otherwise method-specific panels (Milling Direction, cost model, …)
      // that guard on `manufacturingMethod === <enum>` reappear on reload.
      return {
        ...config,
        manufacturingMethods: [],
        manufacturingMethod: undefined,
        millingVector: undefined,
        drawDirectionVector: undefined,
      };
    }
    const repaired = repairCorruptedCases(config.manufacturingMethods);

    // Case labels are derived strings (computeCaseLabel is their only
    // writer). Persisted copies go stale when a method's display name
    // changes (e.g. "Die Casting" → "Casting"), and they propagate into
    // children summaries and completion emails — refresh them on load.
    let labelsChanged = false;
    const relabeled = repaired.map((c) => {
      const label = computeCaseLabel(c);
      if (c.label === label) return c;
      labelsChanged = true;
      return { ...c, label };
    });

    if (repaired === config.manufacturingMethods && !labelsChanged) return config;
    const cases = labelsChanged ? relabeled : repaired;
    return {
      ...config,
      manufacturingMethods: cases,
      manufacturingMethod: cases[0].method,
    };
  }

  const legacy = config.manufacturingMethod;

  if (Array.isArray(legacy)) {
    const cases = legacy.map((entry) => caseFromLegacyEntry(entry));
    return {
      ...config,
      manufacturingMethods: cases,
      manufacturingMethod: cases[0]?.method ?? 'Additive Manufacturing',
    };
  }

  // Legacy single-method projects: lift the flat field into a one-case array.
  if (typeof legacy === 'string') {
    const lifted: ManufacturingCase = {
      methodId: uuidv4(),
      method: legacy,
    };

    if (config.millingVector !== undefined) lifted.millingVector = config.millingVector;
    if (config.drawDirectionVector !== undefined) lifted.drawDirectionVector = config.drawDirectionVector;
    if (config.generatePrismatic !== undefined) lifted.generatePrismatic = config.generatePrismatic;
    if (config.costModel !== undefined) lifted.costModel = config.costModel;
    if (config.minimumToolSize !== undefined) lifted.minimumToolSize = config.minimumToolSize;
    if (config.minimumToolSizeUnit !== undefined) lifted.minimumToolSizeUnit = config.minimumToolSizeUnit;
    if (config.ribsDensity !== undefined) lifted.ribsDensity = config.ribsDensity;
    if (config.wallThickness !== undefined) lifted.wallThickness = config.wallThickness;
    if (config.bottomCutFrac !== undefined) lifted.bottomCutFrac = config.bottomCutFrac;
    if (config.skeletonMethod !== undefined) lifted.skeletonMethod = config.skeletonMethod;

    return {
      ...config,
      manufacturingMethods: [lifted],
    };
  }

  // Brand-new project with no method specified — start empty.
  // The user must pick a manufacturing method before optimization is enabled
  // (see canOptimize in features/flow/optimization/utils/validation.util.ts).
  return {
    ...config,
    manufacturingMethods: [],
  };
}
